import React from "react"
import { CompetitionMarketingFormContentProps, CompetitionMarketingFormEntryMessages, MarketingFormViewState } from "../../../types/cms/competition-marketing-form"
import { Text, Stack, Flex, Button, Box } from "../../../vanilla"
import { CmsImage } from "../cms-image"
import { Spinner } from "../../spinner"
import { CompetitionMarketingFormFields } from '../../forms/competition-marketing-form-fields/index'
import useNavigation from "../../../hooks/use-navigation"

export const CompetitionMarketingFormContent = React.forwardRef((props: CompetitionMarketingFormContentProps, ref: React.Ref<HTMLDivElement>) => {
  const {
    state,
    form,
    onSubmit,
    handleClose,
    isModal,
    title,
    titleColor,
    description,
    formFields,
    formCheckboxes,
    formEntryMessages = {},
    isLoading,
    isPreviouslySubmitted,
    terms,
    stepper,
    currentStep = 1,
    totalSteps = 3,
    onNext,
    onBack,
    isLastStep = false,
    isValidating = false,
    isSubmitting = false,
    ...rest
  } = props

  const {
    failedSubmissionDescription,
    failedSubmissionTitle,
    previouslySubmittedDescription,
    previouslySubmittedTitle,
    successIcon,
    successMessageDescription,
    successMessageTitle,
    endedMessageTitle,
    endedMessageDescription
  } = formEntryMessages as CompetitionMarketingFormEntryMessages

  const navigate = useNavigation()
  let stateTitle = previouslySubmittedTitle
  let stateDescription = previouslySubmittedDescription
  let buttonText = 'Continue Shopping'

  switch (state) {
    case MarketingFormViewState.ERROR:
      stateTitle = failedSubmissionTitle
      stateDescription = failedSubmissionDescription
      buttonText = 'Try Again'
      break
    case MarketingFormViewState.ENDED:
      stateTitle = endedMessageTitle
      stateDescription = endedMessageDescription
      break
    case MarketingFormViewState.SUCCESS:
      stateTitle = successMessageTitle
      stateDescription = successMessageDescription
      break
  }

  const isSubmitDisabled = () => {
    // Only check on last step or in regular mode
    if (stepper?.stepperFunctionality && !isLastStep) {
      return false
    }

    const addressValue = form.watch('address')
    const firstCheckboxValue = form.watch('marketingPermission1')
    const addressLine1Value = form.watch('address1')
    const postalCodeValue = form.watch('postalCode')
    const hasAddressError = !!form.formState.errors.address
    const hasSearchForAddressError = !!form.formState.errors.searchForAddress

    return !addressValue || !firstCheckboxValue || hasAddressError || hasSearchForAddressError || !addressLine1Value || !postalCodeValue
  }

  // Render step heading and subheading based on current step
  const renderStepHeading = () => {
    const stepHeadings = [
      {
        heading: description,
        subheading: stepper?.stepperForm?.page1Subheading
      },
      {
        heading: stepper?.stepperForm?.page2Heading,
        subheading: stepper?.stepperForm?.page2Subheading
      },
      {
        heading: stepper?.stepperForm?.page3Heading,
        subheading: stepper?.stepperForm?.page3Subheading
      }
    ]

    const currentHeading = stepHeadings[currentStep - 1]
    if (!currentHeading) return null

    return (
      <>
        {currentHeading.heading && (
          <Text variant="heading1">
            {currentHeading.heading}
          </Text>
        )}
        {currentHeading.subheading && (
          <Text variant="text3" color="textMuted">
            {currentHeading.subheading}
          </Text>
        )}
      </>
    )
  }

  // Render the Success / Error page
  if (state !== MarketingFormViewState.INITIAL || isPreviouslySubmitted) {
    return (
      <>
        <Stack
          align="center"
          justify="center"
          bg="white"
          spacing="16px"
          width="400px"
          maxWidth="450px"
          height="400px"
          maxHeight="450px"
          paddingX="24px"
          paddingY="36px"
          marginX="auto"
          {...rest}
          ref={ref}
        >
          {isLoading && (
            <Spinner color="primary"/>
          )}
          {successIcon && state !== MarketingFormViewState.ERROR && (
            <CmsImage images={successIcon.images} width="80px" style={{objectFit: 'cover'}} />
          )}
          <Stack width="full" align="center" spacing="24px" >
              <Text align="center" variant="heading4" lineHeight='normal' color='gray1000' >
                {stateTitle}
              </Text>
              <Text variant="text3" marginBottom="16px" align="center" color='gray1000'>
                {stateDescription}
              </Text>
          </Stack>

          <Flex justifyContent="center" width='full'>
            <Button
              onClick={() => {
                if (state === MarketingFormViewState.ERROR || isModal) {
                  return handleClose()
                }
                if (state === MarketingFormViewState.SUCCESS || !isModal) {
                  navigate('/offers')
                }
              }}
              width="full"
            >
              {buttonText}
            </Button>
          </Flex>
        </Stack>
      </>
    )
  }
  
  return (
    <Stack
      bg="white"
      width="400px"
      minHeight="400px"
      paddingX='20px'
      paddingTop={!props.isModal ? '20px' : '0px'}
      paddingBottom="8px"
      marginX="auto"
      {...rest}
      ref={ref}
    >
      {/* If the Stepper functionality is used, break form into steps */}
      {stepper?.stepperFunctionality ? (
        <>
          {renderStepHeading()}
          <Box 
            as="form"
            display="flex"
            flexDirection="column"
            flex="1"
            marginTop="16px"
          >
            <CompetitionMarketingFormFields 
              form={form} 
              currentStep={currentStep}
              stepperMode={true}
              stepContent={stepper.stepperForm}
              stepConfig={props.stepConfig}
              formCheckboxes={formCheckboxes}
              {...formFields} 
            />
            
            <Stack spacing="4px" marginTop="auto">
              <Flex gap="12px">
                {currentStep > 1 ? (
                  <Button
                    size="md"
                    variant="outlinePrimary"
                    onClick={onBack}
                    type="button"
                    width="full"
                  >
                    Back
                  </Button>
                ) : null}
                
                <Button 
                  size="md" 
                  onClick={isLastStep ? props.onManualSubmit : onNext}
                  type="button"
                  width="full"
                  isLoading={isLastStep ? isSubmitting : isValidating}
                  disabled={isLastStep ? (isSubmitDisabled() || isSubmitting) : isValidating}
                >
                  {isLastStep ? 'Submit' : 'Continue'}
                </Button>
              </Flex>
              
              <Flex justifyContent="center">
                <Text variant="text1" color="gray1000" >
                  Step {currentStep} of {totalSteps}
                </Text>
              </Flex>
            </Stack>
          </Box>
        </>
      ) : (
        // Regular form rendering
        <>
          <Text
            as="h4"
            variant="heading4"
            lineHeight="taller"
            marginBottom="16px"
            style={{color: titleColor?.color}}
          >
            {title}
          </Text>
          {description && (
            <Text variant="text6" marginBottom="16px">
              {description}
            </Text>
          )}
          <form onSubmit={onSubmit}>
            <CompetitionMarketingFormFields 
              form={form}
              formCheckboxes={formCheckboxes}
              {...formFields} 
            />
            <Flex justifyContent="center" marginTop="16px">
              <Button 
                size="md" 
                type="submit" 
                width="88px"
                isLoading={isSubmitting}
                disabled={isSubmitDisabled() || isSubmitting}
              >
                Submit
              </Button>
            </Flex>
          </form>
        </>
      )}
    </Stack>
  )
})